import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import DescriptionContainer from '@/components/DescriptionList/DescriptionContainer';
import DescriptionRow from '@/components/DescriptionList/DescriptionRow';
import { H4 } from '@/components/Element/Heading';
import VerticalLeadFormSection from '@/components/LeadForm/VerticalLeadFormSection';
import MetaTags from '@/components/Head/MetaTags';
import { useRouter } from 'next/router';
import DrawerButton from '@/components/Button/DrawerButton';
import { Provider } from 'data/types';
import { getProviderBySlug } from 'data/database';
// import { ProductJsonLd } from 'next-seo';
import DescirptionContainer from '@/components/DescriptionList/DescriptionContainer';
import { OrganizationJsonLd } from 'next-seo';
import SolarHome from '@/images/popup/house.png';
import Image from 'next/legacy/image';
import HowLongCanASolarBatteryPowerAHouse from 'images/solar-faq/How Long Can A Solar Battery Power Your House.png';
import CanIUseSolarEnergyInAPowerOutage from 'images/solar-faq/Can I Use Solar In A Power Outage_.png';
import SolarGuideHero from '@/images/solar-guide/Home Solar Guide.png';
import Center from '@/components/Element/Center';
import ResourceBlock from '@/components/ResourceBlock/ResourceBlock';
import UtilityHeaderSection from '@/components/StateHeaderSection/UtilityHeaderSection';
import Popdown from '@/components/Popdown/Popdown';
import Head from 'next/head';

export async function getStaticPaths() {
  return {
    paths: [],

    fallback: 'blocking', // false or 'blocking'
  };
}

type StaticProps = {
  params: {
    provider_slug: string;
    // vehicle_slug: string;
  };
};
const resources = [
  {
    imageSrc: SolarGuideHero,
    href: '/home-solar-guide',
    title: 'Home Solar Guide',
    description:
      'Explore the value of solar panels for your home in our comprehensive 2023 guide.',
  },
  {
    imageSrc: HowLongCanASolarBatteryPowerAHouse,
    href: '/solar-faq/how-long-can-a-solar-battery-power-your-house',
    title: 'How Long Can A Solar Battery Power A House?',
    description:
      'A solar battery‘s ability to power a house depends on its capacity (measured in kilowatt-hours, kWh) and the home‘s energy consumption. On average, a fully charged 10 kWh battery can power a house for 12-24 hours, and even longer with careful budgeting.',
  },
  {
    imageSrc: CanIUseSolarEnergyInAPowerOutage,
    href: '/solar-faq/can-i-use-solar-energy-during-a-power-outage',
    title: 'Can I Use Solar Energy In a Power Outage?',
    description:
      'You can only use solar energy during a power outage if you have a battery storage system. Without a battery system, your solar panels won‘t work to ensure the safety of utility workers who are working to restore power.',
  },
];

export async function getStaticProps({
  params: { provider_slug },
}: StaticProps) {
  const provider = await getProviderBySlug(provider_slug);
  // const vehicle = await getVehicleBySlug(vehicle_slug);
  // console.log(provider);
  return { props: { provider } };
}
type Props = {
  provider: Provider;
};

export default function Page({ provider }: Props) {
  const router = useRouter();
  const canonicalUrl = `https://energypal.com${router.asPath}`;
  const citiesArray = Array.isArray(provider.cities) ? provider.cities : [];

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  return (
    <article>
      <Head>
        <link rel="canonical" href={canonicalUrl.split('?')[0]} />
      </Head>
      <MetaTags
        title={`${provider.provider_name} Utilities and Outages`}
        description={`${provider.provider_name} outage hotline and support phone number. Find comprehensive data on the utility rates, average monthly bills and outages`}
      />
      <OrganizationJsonLd
        type="Corporation"
        name={provider.provider_name}
        legalName={provider.provider_name}
        // logo={make.webpPath}
        address={provider.headquarters}
        url={`https://energypal.com/utilities/${provider.slug}`}
      />
      {/* <SimpleCenteredHeaderSection
        dark
        supTitle={`Utilities and outage information ${YEAR}`}
        title={provider.provider_name}
        subTitle="Explore utility companies with EnergyPal. Get contact details, outage information, and see rates effortlessly."
      /> */}
      <UtilityHeaderSection provider={provider.provider_name} />

      <Popdown
        title="Tired of outages?"
        subtitle="Power your home with solar energy"
      />
      <div className="hidden md:block">
        <FullWidthBarBreadcrumbs
          pages={[
            {
              name: 'Resources',
              href: '/solar-customer-resources',
            },
            {
              name: 'Utilities',
              href: '/utilities',
            },
            {
              name: `${provider.provider_name}`,
              href: `/utilities/${provider.slug}`,
            },
          ]}
        />
      </div>
      <div className="md:hidden">
        <FullWidthBarBreadcrumbs
          pages={[
            {
              name: 'Utilities',
              href: '/utilities',
            },
            {
              name: `${provider.provider_name}`,
              href: `/utilities/${provider.slug}`,
            },
          ]}
        />
      </div>
      <CenteredContentSection>
        <p>
          <strong>{provider.provider_name}</strong>, a {provider.company_type}{' '}
          utility provider located at <strong>{provider.headquarters}</strong>,
          has been a cornerstone in serving the energy needs of the community.
          As a crucial lifeline, {provider.provider_name} has been providing
          reliable and affordable electricity to residents, businesses, and
          industries alike. This company page will cover the company&apos;s
          services, its customer base, and the rates they offer, outage hotline
          and outage map information.
        </p>

        <DescirptionContainer>
          <DescriptionRow label="Name">{provider.provider_name}</DescriptionRow>
          <DescriptionRow label="Website">{provider.website}</DescriptionRow>
          <DescriptionRow label="Address">
            {provider.headquarters}
          </DescriptionRow>
          <DescriptionRow label="Customer Service Phone">
            <a href={`tel:${provider.customer_service_phone}`}>
              {provider.customer_service_phone}
            </a>
          </DescriptionRow>
        </DescirptionContainer>

        <H4>Outage Information</H4>
        <p>
          Outages can be incredibly frustrating, disrupting our daily lives and
          causing immense inconvenience. These unplanned interruptions in
          services, whether they affect electricity, internet, or other
          essential utilities, can occur for various reasons.
        </p>

        <p>
          {provider.provider_name} understands the importance of managing power
          outages promptly and efficiently. To report an outage or receive
          updates on ongoing outages, customers can contact the Outage Hotline
          at{' '}
          <strong>
            <a href={`tel:${provider.outage_phone}`}>{provider.outage_phone}</a>
          </strong>
          . The dedicated customer service team is available at{' '}
          <strong>
            <a href={`tel:${provider.customer_service_phone}`}>
              {provider.customer_service_phone}
            </a>
          </strong>{' '}
          to address any concerns or inquiries related to utility services. See
          full outage details below.
        </p>
        <DescirptionContainer>
          <DescriptionRow label="Outage Hotline Phone">
            <a href={`tel:${provider.outage_phone}`}>{provider.outage_phone}</a>
          </DescriptionRow>
          <DescriptionRow label="Outage Map">
            {provider.outage_map}
          </DescriptionRow>
        </DescirptionContainer>
        <div className="bg-primary pb-8 pt-1 rounded-lg shadow-lg text-center">
          <div className="text-white">
            <h3 className="text-3xl font-bold mb-2 pt-0 text-white">
              Tired of Outages?
            </h3>
            <div className="w-40 h-40 mx-auto">
              <Image src={SolarHome} alt="home with solar" />
            </div>
            <h5 className="text-lg mb-4 mt-[-40px]">
              Power your home with solar energy
            </h5>
          </div>
          <DrawerButton>Get A FREE Quote</DrawerButton>
        </div>

        <H4>Company Information</H4>
        <DescirptionContainer>
          <DescriptionRow label="Company Type">
            {provider.company_type}
          </DescriptionRow>
          <DescriptionRow label="CEO">{provider.ceo}</DescriptionRow>
          <DescriptionRow label="Total Production">
            {provider.total_production}
          </DescriptionRow>
          <DescriptionRow label="Renewable Production">
            {provider.production_renewable_energy}
          </DescriptionRow>
          <DescriptionRow label="Non-Renewable Production">
            {provider.production_non_renewable_energy}
          </DescriptionRow>
          <DescriptionRow label="Parent Company">
            {provider.parent_company}
          </DescriptionRow>
          {/* <DescriptionRow label="Subsidiary Company">
            {provider.subsidiary_companies}
          </DescriptionRow> */}
        </DescirptionContainer>
        <Center>
          <H4>
            Learn How Home Solar Can Help With {provider.provider_name} Outages
          </H4>
        </Center>
        <div className="grid max-w-lg gap-3 mx-auto mt-4 lg:grid-cols-3 lg:max-w-none">
          {/* <ResourceLinkBlock title="Solar Overview" /> */}
          {resources.map((resource) => (
            <ResourceBlock key={resource.title} {...resource} />
          ))}
        </div>
        <H4>
          {provider.provider_name} Residential Utility Rates & Information
        </H4>
        <p>
          The current utility rates provided by {provider.provider_name} are
          easily accessible to customers through various channels, such as their
          official website, mobile applications, and customer service hotline.
          These up-to-date rates enable consumers to make informed decisions
          about their energy usage and expenses. The company&apos;s transparent
          approach to sharing rate information empowers customers to stay
          informed about any fluctuations or adjustments in rates for
          residential, commercial, and industrial sectors. Moreover,{' '}
          {provider.provider_name} may offer flexible time-of-use (TOU) or peak
          and off-peak pricing plans, providing customers with the opportunity
          to adjust their energy consumption based on rate variations throughout
          the day. This allows consumers to potentially optimize their
          electricity usage and reduce costs.
        </p>
        <p>
          Businesses and industries, especially those with higher energy
          consumption, find this real-time rate data invaluable for analyzing
          their electricity usage patterns and identifying cost-saving
          opportunities. Overall, {provider.provider_name}&apos;s commitment to
          providing accessible and unbiased rate information helps foster a
          sense of trust and transparency between the company and its customers.
        </p>
        <p>
          Being informed about current utility rates can help you make the
          decision of whether solar energy is a viable option for your energy
          needs. By understanding the fluctuating costs of conventional
          electricity, you can explore the potential benefits of switching to
          solar power.
        </p>
        <p>See below for {provider.provider_name} residential utility rates:</p>
        <DescriptionContainer>
          <DescriptionRow label="Average Residential Rate">
            {provider.avg_residential_kwh_rate}
          </DescriptionRow>
          <DescriptionRow label="Average Residential Electricty Bill">
            {provider.avg_residential_electricity_bill}
          </DescriptionRow>
          <DescriptionRow label="Residential Customers">
            {provider.residential_customers}
          </DescriptionRow>
          <DescriptionRow label="Residential Sales">
            {provider.residential_sales}
          </DescriptionRow>
        </DescriptionContainer>
      </CenteredContentSection>
      <CenteredContentSection>
        <H4>{provider.provider_name} Commercial Utility Rates & Information</H4>
        <DescriptionContainer>
          <DescriptionRow label="Average Commercial Rate">
            {provider.avg_commercial_kwh_rate}
          </DescriptionRow>
          <DescriptionRow label="Average Commercial Electricity Bill">
            {provider.avg_commercial_electricity_bill}
          </DescriptionRow>
          <DescriptionRow label="Commercial Sales">
            {provider.commercial_sales}
          </DescriptionRow>
          <DescriptionRow label="Commercial Customers">
            {provider.commercial_customers}
          </DescriptionRow>
        </DescriptionContainer>
      </CenteredContentSection>
      <CenteredContentSection>
        <H4>{provider.provider_name} Industrial Utility Rates & Information</H4>
        <DescriptionContainer>
          <DescriptionRow label="Average Industrial Rate">
            {provider.avg_industrial_kwh_rate}
          </DescriptionRow>
          <DescriptionRow label="Average Industrial Electricity Bill">
            {provider.avg_industrial_electricity_bill}
          </DescriptionRow>
          <DescriptionRow label="Industrial Sales">
            {provider.industrial_sales}
          </DescriptionRow>
          <DescriptionRow label="Industrial Customers">
            {provider.industrial_customers}
          </DescriptionRow>
        </DescriptionContainer>
        <p>
          In conclusion, being informed about current utility rates empowers
          individuals to make educated decisions about transitioning to solar
          energy. The potential for long-term cost savings and the positive
          impact on the environment make solar power an attractive alternative
          to conventional electricity. By staying up-to-date with utility rates
          and exploring the incentives available, consumers can confidently
          assess whether solar energy aligns with their financial goals and
          environmental values.
        </p>
        <H4>{provider.provider_name} City Coverage</H4>
        <div>
          {citiesArray.map((city: string, index: number) => (
            <li key={index}>{city}</li>
          ))}
        </div>
      </CenteredContentSection>
      <VerticalLeadFormSection />
    </article>
  );
}
